import React, { useEffect, useRef, useState } from 'react';
import { getLiveTranscriptWsUrl, getCurrentUserIdForCalls } from '../../lib/apiConfig';
import { transcriptLines } from './api';

/* Realtime transcript panel — subscribes to the /live-transcript WebSocket on the voice server
   and renders call messages as they are produced (TEN outbound path broadcasts per turn). */
const LiveTranscript = ({ ownerId, className = '' }) => {
  const scopedOwner = ownerId !== undefined ? ownerId : getCurrentUserIdForCalls() || null;
  const [lines, setLines] = useState([]);
  const [status, setStatus] = useState('connecting'); // connecting | live | closed
  const [active, setActive] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let ws;
    let retry;
    let closed = false;

    const connect = () => {
      try {
        ws = new WebSocket(getLiveTranscriptWsUrl(scopedOwner));
      } catch (_) {
        setStatus('closed');
        return;
      }
      ws.onopen = () => setStatus('live');
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'update') {
            setLines(transcriptLines({ transcript: msg.transcript }));
            setActive(!!msg.active);
          }
        } catch (_) {}
      };
      ws.onclose = () => {
        if (closed) return;
        setStatus('closed');
        retry = setTimeout(connect, 3000);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch (_) {}
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(retry);
      try {
        ws && ws.close();
      } catch (_) {}
    };
  }, [scopedOwner]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [lines]);

  const statusLabel =
    status === 'live' ? (active ? 'Call in progress' : 'Waiting for a call') : status === 'connecting' ? 'Connecting…' : 'Reconnecting…';

  return (
    <div className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 ${className}`}>
      <div className="flex shrink-0 items-center justify-between border-b border-slate-100 bg-slate-50/60 px-3.5 py-2">
        <span className="text-[12.5px] font-semibold">Live transcript</span>
        <span
          className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium ${
            status === 'live' ? 'text-emerald-600' : 'text-slate-400'
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              status === 'live' ? (active ? 'animate-pulse bg-emerald-500' : 'bg-emerald-400') : 'bg-slate-300'
            }`}
          />
          {statusLabel}
        </span>
      </div>
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-slate-50/30 p-3">
        {lines.length === 0 ? (
          <p className="py-6 text-center text-[12px] text-slate-400">Transcript will appear here as the call runs.</p>
        ) : (
          lines.map((l, i) => (
            <div key={i} className={`flex ${l.from === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-[12.5px] leading-relaxed ${
                  l.from === 'user'
                    ? 'rounded-br-sm bg-teal-600 text-white'
                    : 'rounded-bl-sm bg-white text-slate-700 shadow-sm ring-1 ring-slate-100'
                }`}
              >
                {l.text}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LiveTranscript;
